/*
 * File: UIFilterForm.js
 * Project: mp-mobile-app
 *
 * Created by Vamsi Sureddy
 *
 * Copyright (c) 2019 MacPractice, Inc. All rights reserved.
 *
 * Description: Controls and styles the different types of inputs a filter form can have
 */
import React from "react";
import { observer, PropTypes as mobxProbTypes } from "mobx-react";
import { withStyles } from "@material-ui/core/styles";
import UISwitch from "../ui/forms/UISwitch";
import UIRadioGroup from "../ui/forms/UIRadioGroup";
import UIDateRadioGroup from "../ui/forms/UIDateRadioGroup";
import { List, ListItem, ListItemText } from "@material-ui/core";
import UISelect from "../ui/UISelect";
import PropTypes from "prop-types";
import UICheckboxGroup from "../ui/forms/UICheckboxGroup";

const styles = theme => ({
  title: {
    backgroundColor: `${theme.palette.common.header} !important`
  },
  filterCategories: {
    fontSize: "1.25rem",
    fontWeight: 400,
    color: theme.palette.common.black
  }
});

const InputControlComponent = ({
  classes,
  field,
  value,
  parentOnChange,
  switchOnChange,
  selectOnChange,
  data
}) => {
  const subtitle = field.label && (
    <ListItem>
      <ListItemText
        primary={field.label}
        primaryTypographyProps={{
          variant: "subtitle1",
          className: classes.filterCategories
        }}
      />
    </ListItem>
  );
  switch (field.type) {
    case "checkboxgroup":
      return (
        <React.Fragment>
          {subtitle}
          <UICheckboxGroup field={field} />
        </React.Fragment>
      );
    case "select":
      return (
        <UISelect
          options={field.options}
          label={field.label}
          value={value}
          parentOnChange={selectOnChange}
          name={field.name}
          fullWidth={true}
        />
      );
    case "radiogroup":
      return (
        <React.Fragment>
          {subtitle}
          <UIRadioGroup
            field={field}
            parentOnChange={parentOnChange}
            value={value}
          />
        </React.Fragment>
      );
    case "dateradiogroup": {
      let end = data["end"];
      let start = data["start"];
      return (
        <React.Fragment>
          {subtitle}
          <UIDateRadioGroup
            field={field}
            value={value}
            parentOnChange={parentOnChange}
            startDate={start}
            endDate={end}
          />
        </React.Fragment>
      );
    }
    case "switch":
      return <UISwitch field={field} onChange={switchOnChange} value={value} />;
    case "header":
      return (
        field.label && (
          <ListItem className={classes.title}>
            <ListItemText
              primary={field.label}
              primaryTypographyProps={{ variant: "h6" }}
            />
          </ListItem>
        )
      );
    default:
      return <div>Not Found</div>;
  }
};

InputControlComponent.propTypes = {
  classes: PropTypes.object.isRequired,
  field: PropTypes.object.isRequired,
  value: PropTypes.any,
  parentOnChange: PropTypes.func,
  switchOnChange: PropTypes.func,
  selectOnChange: PropTypes.func,
  data: PropTypes.object
};

const UIFilterForm = ({ classes, fields, data, parentOnChange }) => {
  let switchOnChange = event => {
    let fieldName = event.target.name;
    let checked = event.target.checked;
    parentOnChange(fieldName, checked);
  };

  let selectOnChange = event => {
    let fieldName = event.target.name;
    let value = event.target.value;
    parentOnChange(fieldName, value);
  };

  return (
    <List>
      {fields.map((field, idx) => {
        var value;
        if (data) {
          value = data[field.name];
        } else {
          value = field.value;
        }
        return (
          <InputControlComponent
            classes={classes}
            field={field}
            key={idx}
            value={value}
            parentOnChange={parentOnChange}
            switchOnChange={switchOnChange}
            selectOnChange={selectOnChange}
            data={data}
          />
        );
      })}
    </List>
  );
};

UIFilterForm.propTypes = {
  classes: PropTypes.object.isRequired,
  fields: PropTypes.oneOfType([PropTypes.array, mobxProbTypes.observableArray])
    .isRequired,
  data: PropTypes.object,
  parentOnChange: PropTypes.func
};

export default withStyles(styles)(observer(UIFilterForm));
